import { useCallback, useEffect, useState } from "react";
import {
  WORK_MODE,
  WORK_MODE_CHANGE_EVENT,
  WORK_MODE_STORAGE_KEY,
} from "../constants/workMode";

function readStoredMode() {
  try {
    const value = window.localStorage.getItem(WORK_MODE_STORAGE_KEY);
    return value === WORK_MODE.OFFLINE ? WORK_MODE.OFFLINE : WORK_MODE.ONLINE;
  } catch {
    return WORK_MODE.ONLINE;
  }
}

function persistMode(mode) {
  try {
    window.localStorage.setItem(WORK_MODE_STORAGE_KEY, mode);
  } catch {
    /* ignore quota / private mode */
  }
  window.dispatchEvent(
    new CustomEvent(WORK_MODE_CHANGE_EVENT, { detail: mode })
  );
}

export default function useWorkMode() {
  const [mode, setMode] = useState(readStoredMode);
  const isOffline = mode === WORK_MODE.OFFLINE;

  useEffect(() => {
    const syncFromStorage = () => setMode(readStoredMode());
    const syncFromEvent = (event) => {
      const next = event?.detail;
      if (next === WORK_MODE.OFFLINE || next === WORK_MODE.ONLINE) {
        setMode(next);
      } else {
        syncFromStorage();
      }
    };

    window.addEventListener("storage", syncFromStorage);
    window.addEventListener(WORK_MODE_CHANGE_EVENT, syncFromEvent);
    return () => {
      window.removeEventListener("storage", syncFromStorage);
      window.removeEventListener(WORK_MODE_CHANGE_EVENT, syncFromEvent);
    };
  }, []);

  const setOfflineEnabled = useCallback((enabled) => {
    const next = enabled ? WORK_MODE.OFFLINE : WORK_MODE.ONLINE;
    persistMode(next);
    setMode(next);
  }, []);

  const toggleWorkMode = useCallback(() => {
    setOfflineEnabled(!isOffline);
  }, [isOffline, setOfflineEnabled]);

  return {
    mode,
    isOffline,
    label: isOffline ? "Offline" : "Online",
    setOfflineEnabled,
    toggleWorkMode,
  };
}
